import { api, desenvolver } from '../api';
import type { ComprobanteDetalle, FiltroDeComprobantes, PaginaComprobantes, Resumen } from '../api';

/**
 * Las tres lecturas de comprobantes (KAN-146), sobre `/api/v1/consultas/**`.
 *
 * # POR QUÉ ESTÁ EN UN ARCHIVO APARTE DE `api.ts`
 *
 * `api.ts` tiene la **única escritura** del módulo: `POST /api/v1/comprobantes`,
 * el mismo endpoint que usan los sistemas conectados. Esto es la otra cara y no
 * se mezcla a propósito, porque la regla de las tres superficies dice que **si
 * escribe, va en `/api/v1/...`**: mientras este archivo solo importe `api.GET`,
 * agregarle una escritura a la pantalla de listado obliga a escribirla en un
 * lugar donde se ve. Un botón de "reemitir" escondido en un listado es un
 * segundo camino de escritura, y acá dos caminos de escritura significan dos
 * comprobantes fiscales por la misma operación — que no se borran: se corrigen
 * con nota de crédito, que es una emisión nueva y va por `api.ts`.
 *
 * Todo esto **lee solo nuestra base; no le pregunta nada a ARCA**. Lo que ARCA
 * tiene y nosotros no es justamente lo que detecta
 * `GET /api/v1/consultas/reconciliacion` (KAN-89), y mezclar las dos fuentes
 * acá haría imposible notar la diferencia.
 */

/**
 * `GET /api/v1/consultas/comprobantes`. Página de comprobantes del tenant, del
 * más reciente al más viejo.
 *
 * El `tenantId` es obligatorio y lo exige el tipo: un listado sin tenant
 * mezclaría comprobantes de contribuyentes distintos, que es lo que el schema
 * multi-tenant existe para evitar.
 *
 * ⚠️ **Al leer, un sistema conectado ve solo su lista blanca.** No cambia nada
 * para esta pantalla —acá siempre pregunta una persona con su JWT— pero explica
 * el `403` que el contrato documenta para este endpoint.
 */
export function listarComprobantes(filtro: FiltroDeComprobantes): Promise<PaginaComprobantes> {
  return desenvolver(api.GET('/api/v1/consultas/comprobantes', { params: { query: filtro } }));
}

/**
 * `GET /api/v1/consultas/comprobantes/{id}`. Un comprobante con su desglose de
 * IVA por alícuota, que el listado no trae.
 *
 * El `id` es el **id local** (el `facturaId` que devolvió la emisión), no el
 * número de comprobante: el número se repite entre puntos de venta y tipos, el
 * id no.
 */
export function obtenerComprobante(id: number): Promise<ComprobanteDetalle> {
  return desenvolver(
    api.GET('/api/v1/consultas/comprobantes/{id}', { params: { path: { id } } }),
  );
}

/**
 * `GET /api/v1/consultas/comprobantes/resumen`. Totales del período por tipo,
 * **agregados en la base**: un resumen anual son decenas de miles de filas y no
 * hay razón para traerlas al navegador para sumarlas ahí (ni forma de sumarlas
 * sin arriesgar un centavo).
 *
 * `desde` y `hasta` son obligatorios en el contrato: un resumen sin período no
 * significa nada.
 */
export function resumenDeComprobantes(
  tenantId: number,
  desde: string,
  hasta: string,
): Promise<Resumen> {
  return desenvolver(
    api.GET('/api/v1/consultas/comprobantes/resumen', {
      params: { query: { tenantId, desde, hasta } },
    }),
  );
}
